import React, { useState } from 'react';
import { Brain, Gift, Users, Calendar, RefreshCw, Sparkles, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { VersionBadge } from './VersionBadge';
import { getShortVersion } from '../utils/version';
import type { FinalRecommendations as FinalRecommendationsType, GiverProfile, RecipientProfile, OccasionContext } from '../types';
import { giverArchetypes, recipientArchetypes } from '../data/archetypes';

interface FinalRecommendationsComponentProps {
  recommendations: FinalRecommendationsType;
  giverProfile: GiverProfile;
  recipientProfile: RecipientProfile;
  occasionContext: OccasionContext;
  onRestart: () => void;
}

export const FinalRecommendationsComponent: React.FC<FinalRecommendationsComponentProps> = ({
  recommendations,
  giverProfile,
  recipientProfile,
  occasionContext,
  onRestart
}) => {
  const [email, setEmail] = useState('');
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [emailMessage, setEmailMessage] = useState('');

  const giverArchetype = giverArchetypes[giverProfile.archetype];
  const recipientArchetype = recipientArchetypes[recipientProfile.archetype];

  const handleSendEmail = async () => {
    if (!email.trim() || isEmailSending) return;

    setIsEmailSending(true);
    setEmailStatus('idle');
    setEmailMessage('');

    try {
      // Prepare the report data
      const reportData = {
        giverProfile: {
          name: giverProfile.name,
          primaryArchetype: giverProfile.archetype,
          confidence: giverProfile.confidence,
          reasoning: giverProfile.reasoning,
          personalizedInsights: giverProfile.insights,
          giftSuggestionThemes: giverProfile.traits // Using traits as gift themes
        },
        recipientProfile: {
          name: recipientProfile.name,
          primaryArchetype: recipientProfile.archetype,
          confidence: recipientProfile.confidence,
          reasoning: recipientProfile.reasoning,
          personalizedInsights: recipientProfile.insights,
          relationship: `${recipientProfile.relationship.type} (${recipientProfile.relationship.duration})`
        },
        occasionAnalysis: {
          occasionType: occasionContext.type,
          budget: `$${occasionContext.budget}`,
          desiredEmotions: occasionContext.emotionGoals,
          giftRecommendations: recommendations.recommendations.map(rec => ({
            title: rec.title,
            description: rec.description,
            reasoning: rec.reasoning,
            archetypeAlignment: rec.psychologicalFit
          })),
          psychologicalSummary: recommendations.psychologicalSummary
        }
      };

      const response = await fetch('/api/send-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          reportData
        })
      });

      const result = await response.json();

      if (response.ok) {
        setEmailStatus('success');
        setEmailMessage(result.message || 'Report sent successfully!');
        setEmail(''); // Clear the email field
      } else {
        setEmailStatus('error');
        setEmailMessage(result.error || 'Failed to send email');
      }
    } catch (error) {
      setEmailStatus('error');
      setEmailMessage('Network error. Please try again.');
      console.error('Email send error:', error);
    } finally {
      setIsEmailSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendEmail();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Gift className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold text-gray-800">GVVR</span>
            <VersionBadge />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Your Personalized Gift Recommendations
          </h1>
          <p className="text-gray-600 text-lg">
            Based on sophisticated psychological analysis of both giver and recipient
          </p>
        </div>

        {/* Psychological Summary */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Brain className="w-6 h-6 text-purple-600" />
            <h2 className="text-2xl font-bold text-gray-800">Psychological Insight</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Giver Profile */}
            <div className="text-center p-4 bg-gray-50 rounded-2xl">
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r ${giverArchetype.color} text-white mb-3`}>
                <Gift className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{giverProfile.name} (Giver)</h3>
              <p className="text-sm font-medium text-indigo-600 mb-2">{giverProfile.archetype}</p>
              <p className="text-xs text-gray-600">{giverProfile.confidence}% confidence</p>
            </div>

            {/* Relationship Dynamic */}
            <div className="text-center p-4 bg-gray-50 rounded-2xl">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white mb-3">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Relationship</h3>
              <p className="text-sm font-medium text-pink-600 mb-2">{recipientProfile.relationship.type}</p>
              <p className="text-xs text-gray-600">{recipientProfile.relationship.duration}</p>
            </div>

            {/* Recipient Profile */}
            <div className="text-center p-4 bg-gray-50 rounded-2xl">
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r ${recipientArchetype.color} text-white mb-3`}>
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{recipientProfile.name} (Recipient)</h3>
              <p className="text-sm font-medium text-purple-600 mb-2">{recipientProfile.archetype}</p>
              <p className="text-xs text-gray-600">{recipientProfile.confidence}% confidence</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-xl">
              <h4 className="font-semibold text-purple-800 mb-2">Psychological Summary</h4>
              <p className="text-purple-700">{recommendations.psychologicalSummary}</p>
            </div>

            <div className="p-4 bg-indigo-50 rounded-xl">
              <h4 className="font-semibold text-indigo-800 mb-2">Giver-Recipient Dynamic</h4>
              <p className="text-indigo-700">{recommendations.giverRecipientDynamic}</p>
            </div>

            <div className="p-4 bg-blue-50 rounded-xl">
              <h4 className="font-semibold text-blue-800 mb-2">Occasion Context</h4>
              <p className="text-blue-700">{recommendations.occasionFit}</p>
            </div>
          </div>
        </div>

        {/* Gift Recommendations */}
        <div className="space-y-6 mb-8">
          {recommendations.recommendations.map((rec, index) => (
            <div key={index} className="bg-white rounded-3xl shadow-xl p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {index + 1}
                  </div>
                </div>
                
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-2xl font-bold text-gray-800">{rec.title}</h3>
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                      {rec.category}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-lg mb-4 leading-relaxed">
                    {rec.description}
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="p-4 bg-green-50 rounded-xl">
                      <h4 className="font-semibold text-green-800 mb-2">Why This Works</h4>
                      <p className="text-green-700 text-sm">{rec.reasoning}</p>
                    </div>
                    
                    <div className="p-4 bg-amber-50 rounded-xl">
                      <h4 className="font-semibold text-amber-800 mb-2">Psychological Fit</h4>
                      <p className="text-amber-700 text-sm">{rec.psychologicalFit}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {rec.tags.map((tag, tagIndex) => (
                      <span 
                        key={tagIndex}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Occasion Details */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-6 h-6 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-800">Occasion Details</h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-2xl">
              <h3 className="font-semibold text-gray-800 mb-2">Occasion</h3>
              <p className="text-green-600 font-medium">{occasionContext.type}</p>
            </div>
            
            <div className="text-center p-4 bg-gray-50 rounded-2xl">
              <h3 className="font-semibold text-gray-800 mb-2">Budget</h3>
              <p className="text-green-600 font-medium">${occasionContext.budget}</p>
            </div>
            
            <div className="text-center p-4 bg-gray-50 rounded-2xl">
              <h3 className="font-semibold text-gray-800 mb-2">Timeline</h3>
              <p className="text-green-600 font-medium">{occasionContext.timeline}</p>
            </div>
            
            <div className="text-center p-4 bg-gray-50 rounded-2xl">
              <h3 className="font-semibold text-gray-800 mb-2">Emotional Goals</h3>
              <p className="text-green-600 font-medium text-sm">{occasionContext.emotionGoals.join(', ')}</p>
            </div>
          </div>
        </div>

        {/* Email Report Section */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">Send Full Report</h2>
          </div>
          
          <p className="text-gray-600 mb-6">
            Get a beautifully formatted HTML report with your complete psychological analysis sent to your email.
          </p>

          <div className="max-w-md mx-auto">
            <div className="flex gap-3">
              <div className="flex-grow">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  disabled={isEmailSending}
                />
              </div>
              <button
                onClick={handleSendEmail}
                disabled={!email.trim() || isEmailSending}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors flex items-center gap-2"
              >
                {isEmailSending ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {isEmailSending ? 'Sending...' : 'Send'}
              </button>
            </div>

            {/* Status Messages */}
            {emailStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-green-700">{emailMessage}</p>
              </div>
            )}

            {emailStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-700">{emailMessage}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="text-center">
          <button
            onClick={onRestart}
            className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors text-lg"
          >
            <RefreshCw className="w-5 h-5" />
            Start New Analysis
          </button>
          
          <p className="text-gray-500 text-sm mt-4">
            This is a demo of GVVR's AI-powered gift curation system - Version {getShortVersion()}
          </p>
        </div>
      </div>
    </div>
  );
}; 